import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";

export type MarketingAgentMode = "shopper" | "split" | "off";

export interface MarketingAgentModeState {
  mode: MarketingAgentMode;
  /** Whether the Shopper Agent option is available to this team via rollout gate. */
  shopperAvailable: boolean;
  /** Whether the Split Test option is available to this team via rollout gate. */
  splitAvailable: boolean;
  /** True until both flag and split-info queries settle. */
  loading: boolean;
}

/**
 * Single source of truth for which marketing agent mode is active.
 *
 * - `shopper` when `team.settings.shopperAgent.enabled` is true.
 * - `split` when a `MarketingAgentSplit` is currently active.
 * - `off` otherwise.
 *
 * Rollout gates (`NEW_MARKETING_AGENT_ONBOARDING` flag, `marketing.marketingAgent.enabled`)
 * only affect which options are *selectable* in Settings — not resolved mode.
 */
export function useMarketingAgentMode(): MarketingAgentModeState {
  const team = useRequiredContext(TeamContext);
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);

  const shopperEnabled = team.settings.shopperAgent?.enabled ?? false;
  const splitAvailable =
    team.settings.marketing?.marketingAgent?.enabled ?? false;

  const shopperFlagLoad = useLoad(
    (signal) =>
      marketingRpcClient.getBooleanFeatureFlag(
        { key: FeatureFlagKey.NEW_MARKETING_AGENT_ONBOARDING },
        { signal },
      ),
    [marketingRpcClient],
  );
  const shopperAvailable = shopperFlagLoad.value?.enabled ?? false;

  const { data: splitInfo, isLoading: splitInfoLoading } = useRpcQuery(
    RedoMarketingRpcClientContext,
    {
      method: "getMarketingAgentSplitInfo",
      args: {},
      options: { enabled: splitAvailable },
    },
  );

  const splitActive = splitInfo?.isActive === true;

  const mode: MarketingAgentMode = shopperEnabled
    ? "shopper"
    : splitActive
      ? "split"
      : "off";

  return {
    mode,
    shopperAvailable,
    splitAvailable,
    loading: shopperFlagLoad.pending || (splitAvailable && splitInfoLoading),
  };
}
